import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ContentBox from '../components/ContentBox';

const API_URL = process.env.REACT_APP_API_URL;

const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

function GuideCard({ article }) {
    const isFrankly = article.isFranklyGuide;

    return (
        <Link
            to={article.href}
            className="group flex flex-col bg-offwhite border border-gray-300 shadow-[0_3px_0_0_rgba(0,0,0,0.2)] hover:shadow-[0_3px_0_0_rgba(0,0,0,0.7)] transition-shadow overflow-hidden"
        >
            <div className="relative overflow-hidden bg-gray-200 h-[160px]">
                {article.headerImage ? (
                    <img
                        src={article.headerImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        style={{ imageRendering: 'auto' }}
                        onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextSibling.style.display = 'flex';
                        }}
                    />
                ) : null}
                <div
                    className="w-full h-full items-center justify-center bg-gray-200"
                    style={{ display: article.headerImage ? 'none' : 'flex' }}
                >
                    <img
                        src={`${process.env.PUBLIC_URL}/img/${isFrankly ? 'frankly_news.png' : 'frankly_guides.png'}`}
                        alt=""
                        className="w-full h-full object-cover"
                    />
                </div>

                <div className="absolute top-2 left-2">
                    <span
                        className="font-['Jersey_10'] uppercase text-[11px] tracking-[1px] text-white px-2 py-0.5"
                        style={{
                            backgroundColor: isFrankly ? 'rgba(90,41,41,0.85)' : 'rgba(34,100,34,0.85)',
                            boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)',
                        }}
                    >
                        {article.categoryName}
                    </span>
                </div>
            </div>

            <div className="flex flex-col flex-1 p-3 gap-2">
                <h2
                    className="font-['Jersey_10'] uppercase text-[17px] tracking-[1px] text-gray-900 leading-tight group-hover:text-green-800 transition-colors"
                    style={{
                        textShadow: 'none',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {article.title}
                </h2>

                {article.flavour && (
                    <p
                        className="text-gray-600 text-xs leading-relaxed flex-1"
                        style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {article.flavour}
                    </p>
                )}

                <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-200">
                    <span className="text-xs text-gray-500 font-['Jersey_10'] uppercase tracking-wide">
                        {article.author}
                    </span>
                    <span className="text-[10px] text-gray-400 font-['Jersey_10'] uppercase tracking-wide">
                        {formatDate(article.publicDate)}
                    </span>
                </div>
            </div>
        </Link>
    );
}

function SkeletonCard() {
    return (
        <div className="flex flex-col bg-offwhite border border-gray-300 shadow-[0_3px_0_0_rgba(0,0,0,0.5)] overflow-hidden animate-pulse">
            <div className="h-[160px] bg-gray-200" />
            <div className="p-3 flex flex-col gap-2">
                <div className="h-5 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-full" />
                <div className="h-3 bg-gray-200 rounded w-5/6" />
                <div className="h-3 bg-gray-200 rounded w-4/6" />
                <div className="flex items-center justify-between pt-2 border-t border-gray-200 mt-1">
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/4" />
                </div>
            </div>
        </div>
    );
}

function PaginationButton({ onClick, disabled, active, children }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={[
                'font-["Jersey_10"] uppercase text-[15px] tracking-[1px] px-3 py-1 transition-all',
                active
                    ? 'bg-green-700 text-white shadow-[0_2px_0_0_rgba(0,0,0,0.3)]'
                    : disabled
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-offwhite border border-gray-300 text-gray-700 hover:bg-gray-100 shadow-[0_2px_0_0_rgba(0,0,0,0.2)]',
            ].join(' ')}
        >
            {children}
        </button>
    );
}

function Guides() {
    const [articles, setArticles] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);

    const fetchGuides = useCallback(async (p) => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}articles/guides/all?page=${p}`);
            const data = await response.json();
            if (data.success) {
                setArticles(data.articles);
                setPagination(data.pagination);
            } else {
                toast.error('Failed to load guides');
            }
        } catch {
            toast.error('Could not connect to server');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGuides(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [page, fetchGuides]);

    const goToPage = (p) => {
        if (p < 1 || (pagination && p > pagination.totalPages)) return;
        setPage(p);
    };

    const renderPageButtons = () => {
        if (!pagination || pagination.totalPages <= 1) return null;

        const { totalPages } = pagination;
        const buttons = [];
        const delta = 2;
        const range = [];

        for (let i = Math.max(2, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
            range.push(i);
        }

        const pages = [1, ...range, totalPages].filter((v, i, arr) => arr.indexOf(v) === i);

        let prev = null;
        for (const p of pages) {
            if (prev && p - prev > 1) {
                buttons.push(
                    <span key={`ellipsis-${p}`} className="font-['Jersey_10'] text-gray-400 px-1 self-end pb-1">
                        ...
                    </span>
                );
            }
            buttons.push(
                <PaginationButton key={p} onClick={() => goToPage(p)} active={p === page}>
                    {p}
                </PaginationButton>
            );
            prev = p;
        }

        return buttons;
    };

    return (
        <div className="w-full min-h-screen p-4 lg2:p-6 overflow-hidden">
            <div className="max-w-6xl mx-auto">
                <ContentBox theme="default" tint="green" header="All Guides">
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {Array.from({ length: 15 }).map((_, i) => (
                                <SkeletonCard key={i} />
                            ))}
                        </div>
                    ) : articles.length === 0 ? (
                        <p className="text-gray-600 text-sm">No guides found.</p>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {articles.map((article) => (
                                    <GuideCard key={article.id} article={article} />
                                ))}
                            </div>

                            {pagination && pagination.totalPages > 1 && (
                                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                                    <span className="text-xs text-gray-500 font-['Jersey_10'] uppercase tracking-wide">
                                        Page {pagination.page} of {pagination.totalPages} &mdash; {pagination.totalItems} guides
                                    </span>

                                    <div className="flex items-center gap-1">
                                        <PaginationButton
                                            onClick={() => goToPage(page - 1)}
                                            disabled={!pagination.hasPrevPage}
                                        >
                                            &lsaquo; Prev
                                        </PaginationButton>

                                        {renderPageButtons()}

                                        <PaginationButton
                                            onClick={() => goToPage(page + 1)}
                                            disabled={!pagination.hasNextPage}
                                        >
                                            Next &rsaquo;
                                        </PaginationButton>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </ContentBox>
            </div>
        </div>
    );
}

export default Guides;